import classNames from 'classnames';
import React, { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
	Cell,
	Column,
	Row,
	Table as AriaTable,
	TableBody,
	TableHeader,
} from 'react-aria-components';
import { flushSync } from 'react-dom';

import { Button, Checkbox } from '@ekhodealer/ekho-common/rev';

import type { Selection } from 'react-aria-components';

import ArrowDown24 from '../icons/ArrowDown24';

import './Table.scss';
import tableConstants from './_tableConstants.module.scss';

/** Width of a table column: a fixed pixel value, a percentage string, or a fractional unit (e.g. '1fr'). */
type ColumnSize = number | string;
/** Width that must resolve to a static value (pixels or percentage string, no fractional units). */
type ColumnStaticSize = number | string;

export type SortDirection = 'ascending' | 'descending' | 'none';

export interface SortingProps {
	sortable?: boolean;
	sortDirection?: SortDirection;
	onSort?: (sortDirection: SortDirection) => void;
}

export interface TableColumn<T = unknown> extends SortingProps {
	/* Unique identifier for this column */
	id: string;
	/* The header text for this column */
	title: string;
	/* Function to render the cell content for this column */
	renderCell: (item: T) => React.ReactNode;
	/* Function to render the header content for this column */
	renderHeader?: (sortingProps?: SortingProps) => React.ReactNode;
	/* Whether cells in this column contain interactive elements (buttons, links) */
	isButtonCell?: boolean;
	/* The width of the column */
	width?: ColumnSize;
	/* The default width of the column */
	defaultWidth?: ColumnSize;
	/* The minimum width of the column */
	minWidth?: ColumnStaticSize;
	/* The maximum width of the column */
	maxWidth?: ColumnStaticSize;
}

/*
Example usage:
const columns: TableColumn<User>[] = [
	{
		id: 'name',
		title: 'Name',
		sortable: true, // Automatically renders sortable header with indicators
		sortDirection: 'ascending',
		onSort: (direction) => handleSort('name', direction),
		renderCell: (user) => user.name,
	},
	{
		id: 'actions',
		title: 'Actions',
		isButtonCell: true,  // Column-level flag for interactive cells
		renderCell: (user) => <button>Edit</button>,
		// Custom header renderer (optional - overrides default sortable header)
		renderHeader: () => null,
	}
];
*/

export interface TableProps<T = unknown> {
	/* The columns to display in the table */
	columns: TableColumn<T>[];
	/* The data items to display in the table */
	items: T[];
	/* Unique identifier for each item */
	getItemId: (item: T) => string;
	/* Whether the table supports row selection */
	selectionMode?: 'single' | 'multiple' | 'none';
	/* Currently selected items */
	selectedItems?: Selection;
	/* Callback when selection changes */
	onSelectionChange?: (selectedItems: Selection) => void;
	/* Whether rows are disabled */
	isRowDisabled?: (item: T) => boolean;
	/* Callback when a row is clicked or tapped */
	onRowActivate?: (item: T) => void;
	/* Number of data columns to make sticky on the left.
	   Note: When selectionMode !== 'none', the selection column is always sticky in addition
	   to any columns specified here. */
	stickyLeftCount?: number;
	/* Number of columns to make sticky on the right */
	stickyRightCount?: number;
	/* Additional CSS class name */
	className?: string;
	/* ARIA label for the table */
	'aria-label'?: string;
	/* Optional content to render below the table */
	belowTableContent?: React.ReactNode;
}

interface StickyCounts {
	stickyLeftCount: number;
	stickyRightCount: number;
	numColumns: number;
	numHiddenLeftColumns: number;
}

// Selection checkbox component for individual rows
const RowSelectionCheckbox = ({ itemId, isDisabled }: { itemId: string; isDisabled: boolean }) => {
	return <Checkbox slot='selection' isDisabled={isDisabled} ariaLabel={`Select ${itemId}`} />;
};

// Selection checkbox component for header (select all)
const HeaderSelectionCheckbox = () => {
	return <Checkbox slot='selection' ariaLabel='Select all items' />;
};

export const Table = <T,>({
	columns,
	items,
	getItemId,
	selectionMode = 'none',
	selectedItems,
	onSelectionChange,
	isRowDisabled,
	onRowActivate,
	stickyLeftCount: stickyLeftCountProp = 0,
	stickyRightCount: stickyRightCountProp = 0,
	className,
	'aria-label': ariaLabel,
	belowTableContent,
}: TableProps<T>) => {
	const { stickyLeftCount, stickyRightCount, numColumns, numHiddenLeftColumns } =
		(function getStickyCounts(): StickyCounts {
			/** In order to correctly calculate offsets, columns managed internally
			 * are counted as "hidden" columns.
			 *
			 * Currently the only hidden column is the selection field which appears on the left when the
			 * selectionMode props is set to 'multiple' or 'single'.
			 */
			const numHiddenLeftColumns = selectionMode !== 'none' ? 1 : 0;

			// Calculate effective sticky counts (props override booleans)
			const stickyLeftCount = stickyLeftCountProp + numHiddenLeftColumns;
			const stickyRightCount = stickyRightCountProp;

			return {
				stickyLeftCount,
				stickyRightCount,
				numColumns: columns.length + numHiddenLeftColumns,
				numHiddenLeftColumns,
			};
		})();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const stickyColumnOffsetsRef = useRef<number[]>(new Array(numColumns).fill(0));
	const [, forceOffsetUpdate] = useState(0);

	// Get the scroll container: prefer .table-page-layout if it exists, otherwise use the local scroll container
	const getScrollContainer = useCallback((): {
		container: Element | null;
		isWrappedByPageLayout: boolean;
	} => {
		const pageLayout = scrollContainerRef?.current?.closest('.table-page-layout');

		return {
			container: pageLayout ?? scrollContainerRef.current,
			isWrappedByPageLayout: !!pageLayout,
		};
	}, []);

	const updateScrollIndicators = useCallback((): void => {
		const { container } = getScrollContainer();
		if (!container || !scrollContainerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = container;
		const el = scrollContainerRef.current;

		if (scrollLeft > 0) {
			el.setAttribute('data-scrollable-left', 'true');
		} else {
			el.removeAttribute('data-scrollable-left');
		}

		if (scrollLeft + clientWidth < scrollWidth - 1) {
			el.setAttribute('data-scrollable-right', 'true');
		} else {
			el.removeAttribute('data-scrollable-right');
		}
	}, [getScrollContainer]);

	const updateColumnWidths = useCallback(
		(container: Element, isWrappedByPageLayout: boolean = false): void => {
			const tableCells = container.querySelectorAll('table tbody tr:first-child td');
			if (!tableCells.length) return;

			const baseOffset = isWrappedByPageLayout
				? parseFloat(tableConstants.stickyOffsetWithPageLayout)
				: 0;

			const offsets = stickyColumnOffsetsRef.current;
			let left = 0,
				right = 0,
				offset = 0;
			let changed = false;

			while (left < stickyLeftCount) {
				const newVal = offset + baseOffset;
				if (offsets[left] !== newVal) {
					offsets[left] = newVal;
					changed = true;
				}
				offset += tableCells[left].getBoundingClientRect().width;
				left++;
			}
			offset = 0;
			while (right < stickyRightCount) {
				const index = numColumns - right - 1;
				const newVal = offset + baseOffset;
				if (offsets[index] !== newVal) {
					offsets[index] = newVal;
					changed = true;
				}
				offset += tableCells[index].getBoundingClientRect().width;
				right++;
			}

			if (changed) forceOffsetUpdate((v) => v + 1);
		},
		[numColumns, stickyLeftCount, stickyRightCount],
	);

	useLayoutEffect(() => {
		const { container, isWrappedByPageLayout } = getScrollContainer();
		if (!container) return;

		updateScrollIndicators();
		updateColumnWidths(container, isWrappedByPageLayout);

		// Use ResizeObserver to detect content size changes (items loading, column changes, etc.)
		const resizeObserver = new ResizeObserver(() => {
			updateScrollIndicators();
			flushSync(() => updateColumnWidths(container, isWrappedByPageLayout));
		});

		// Observe both the container and its first child (the table) for size changes
		resizeObserver.observe(container);
		const table = container.querySelector('table');
		if (table) {
			resizeObserver.observe(table);
		}
		container.addEventListener('scroll', updateScrollIndicators);

		return () => {
			resizeObserver.disconnect();
			container.removeEventListener('scroll', updateScrollIndicators);
		};
	}, [getScrollContainer, updateScrollIndicators, updateColumnWidths]);

	const getTrueIndex = (index: number, isHiddenColumn = false): number => {
		if (isHiddenColumn) {
			return index;
		}

		return index + numHiddenLeftColumns;
	};

	const handleSelectionChange = (keys: Selection) => {
		if (onSelectionChange) {
			onSelectionChange(keys);
		}
	};

	// Helper function to determine if a column should be sticky
	const getColumnStickyValue = (
		columnIndex: number,
		totalColumns: number,
	): 'left' | 'right' | undefined => {
		// Left sticky: columns 0 to (stickyLeftCount - 1)
		if (stickyLeftCount > 0 && columnIndex < stickyLeftCount) {
			return 'left';
		}
		// Right sticky: columns (totalColumns - stickyRightCount) to (totalColumns - 1)
		if (stickyRightCount > 0 && columnIndex >= totalColumns - stickyRightCount) {
			return 'right';
		}
		return undefined;
	};

	// Get next sort direction
	const getNextSortDirection = (currentDirection: SortDirection): SortDirection => {
		switch (currentDirection) {
			case 'none':
				return 'descending';
			case 'ascending':
				return 'descending';
			case 'descending':
				return 'ascending';
			default:
				return 'descending';
		}
	};

	// Default sortable header renderer
	const renderDefaultHeader = (column: TableColumn<T>): ReactNode => {
		const { sortable, sortDirection, onSort, title } = column;

		if (!sortable || !sortDirection) {
			return <div className='table-header-title'>{title}</div>;
		}

		let indicator: React.ReactNode;
		switch (sortDirection) {
			case 'ascending':
				indicator = <ArrowDown24 isUp={true} />;
				break;
			case 'descending':
				indicator = <ArrowDown24 isUp={false} />;
				break;
			default:
				indicator = <ArrowDown24 isUp={false} />;
		}

		return (
			<Button
				onPress={() => onSort?.(getNextSortDirection(sortDirection || 'none'))}
				variant={null}
				size='small'
				iconSide='right'
				icon={indicator}
				className={classNames('table-header-button-sortable', {
					'is-sorted': sortDirection !== 'none',
				})}>
				{title}
			</Button>
		);
	};

	const formatColumnSize = (size: ColumnStaticSize | undefined): string | undefined =>
		size ? (typeof size === 'number' ? `${size}px` : size) : undefined;

	// Helper function to determine if a column is the innermost sticky column (the edge)
	const isColumnStickyEdge = (
		columnIndex: number,
		stickyValue: 'right' | 'left' | undefined,
	): boolean => {
		let edgeIndex = -1;

		if (stickyValue === 'right') {
			edgeIndex = numColumns - stickyRightCount;
		} else if (stickyValue === 'left') {
			edgeIndex = stickyLeftCount - 1;
		}

		return columnIndex === edgeIndex;
	};

	return (
		<div
			data-component='table'
			className={classNames({ 'has-below-table-content': !!belowTableContent })}>
			<div ref={scrollContainerRef} className='table-scroll-container'>
				<div className='sticky-left-cover' aria-hidden='true' />
				<AriaTable
					aria-label={ariaLabel}
					selectionMode={selectionMode}
					selectedKeys={selectedItems}
					onSelectionChange={handleSelectionChange}
					selectionBehavior='toggle'
					data-is-selectable={selectionMode !== 'none'}
					className={className}>
					<TableHeader className='table-header'>
						{selectionMode !== 'none' && (
							<Column
								className='table-column table-cell--selection'
								data-is-column-sticky='left'
								data-is-sticky-edge={isColumnStickyEdge(0, 'left') || undefined}
								// eslint-disable-next-line no-restricted-syntax -- Required for sticky features
								style={
									{
										...{
											left: stickyColumnOffsetsRef.current[0],
										},
									} as React.CSSProperties
								}>
								<HeaderSelectionCheckbox />
							</Column>
						)}
						{columns.map((column, index) => {
							const stickyValue = getColumnStickyValue(
								getTrueIndex(index),
								numColumns,
							);
							return (
								<Column
									key={column.id}
									isRowHeader={index === 0}
									allowsSorting={column.sortable}
									className='table-column'
									data-column-id={column.id}
									data-is-column-sticky={stickyValue}
									data-is-sticky-edge={
										isColumnStickyEdge(getTrueIndex(index), stickyValue) ||
										undefined
									}
									// eslint-disable-next-line no-restricted-syntax -- Required for sticky features
									style={
										{
											'--col-min-width': formatColumnSize(column.minWidth),
											'--col-max-width': formatColumnSize(column.maxWidth),
											...{
												[stickyValue ?? '']: stickyValue
													? stickyColumnOffsetsRef.current[
															getTrueIndex(index)
														]
													: '',
											},
										} as React.CSSProperties
									}>
									{column.renderHeader
										? column.renderHeader({
												sortable: column.sortable,
												sortDirection: column.sortDirection,
												onSort: column.onSort,
											})
										: renderDefaultHeader(column)}
								</Column>
							);
						})}
					</TableHeader>
					<TableBody className='table-body'>
						{items.map((item) => {
							const itemId = getItemId(item);
							const isDisabled = isRowDisabled ? isRowDisabled(item) : false;

							return (
								<Row
									key={itemId}
									id={itemId}
									isDisabled={isDisabled}
									onAction={onRowActivate && (() => onRowActivate(item))}
									className='table-row'>
									{selectionMode !== 'none' && (
										<Cell
											className='table-cell table-cell--selection'
											data-is-column-sticky='left'
											data-is-sticky-edge={
												isColumnStickyEdge(0, 'left') || undefined
											}
											// eslint-disable-next-line no-restricted-syntax -- Required for sticky features
											style={
												{
													...{
														left: stickyColumnOffsetsRef.current[0],
													},
												} as React.CSSProperties
											}>
											<RowSelectionCheckbox
												itemId={itemId}
												isDisabled={isDisabled}
											/>
										</Cell>
									)}
									{columns.map((column, index) => {
										const stickyValue = getColumnStickyValue(
											getTrueIndex(index),
											numColumns,
										);
										return (
											<Cell
												key={`${itemId}-${column.id}`}
												className='table-cell'
												data-column-id={column.id}
												data-is-button-cell={column.isButtonCell}
												data-is-column-sticky={stickyValue}
												data-is-sticky-edge={
													isColumnStickyEdge(
														getTrueIndex(index),
														stickyValue,
													) || undefined
												}
												// eslint-disable-next-line no-restricted-syntax -- Required for sticky features
												style={
													{
														'--col-min-width': formatColumnSize(
															column.minWidth,
														),
														'--col-max-width': formatColumnSize(
															column.maxWidth,
														),
														...{
															[stickyValue ?? '']: stickyValue
																? stickyColumnOffsetsRef.current[
																		getTrueIndex(index)
																	]
																: '',
														},
													} as React.CSSProperties
												}>
												{column.renderCell(item)}
											</Cell>
										);
									})}
								</Row>
							);
						})}
					</TableBody>
				</AriaTable>
				<div className='sticky-right-cover' aria-hidden='true' />
			</div>
			{belowTableContent}
		</div>
	);
};
